import React from 'react';

import filter from 'lodash/filter';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, lifecycle, withProps, withState } from 'recompose';

import { siteUrl } from '@sp/config';

import withRouteParams from '../../../../utils/withRouteParams';

import {
  NoPrint,
  PageBreak,
  PrintElement,
  PrintOnly,
} from '../../../../components/PrintElement';
import Breadcrumbs from '../../../../components/Breadcrumbs';
import NavBar from '../../../../components/NavBar';

import CustomerForm from '../CustomerForm';
import QuoteCarInfo from '../QuoteCarInfo';
import PaymentDetails from '../PaymentDetails';

import * as actions from '../../actions';

import s from './styles.less';

const propTypes = {
  isFormVisible: PropTypes.bool.isRequired,
  setFormVisibility: PropTypes.func.isRequired,
  car: PropTypes.object.isRequired,
  car2: PropTypes.object.isRequired,
  quote: PropTypes.shape({
    lastServiceDate: PropTypes.string,
    lastServiceMileage: PropTypes.number,
    VRN2CurrentMileage: PropTypes.number,
    VRN2LastServiceDate: PropTypes.string,
    currentMileage: PropTypes.number,
    VRN: PropTypes.string,
    VRN2: PropTypes.string,
    Provider: PropTypes.shape({
      companyName: PropTypes.string,
      addressLine1: PropTypes.string,
      addressLine2: PropTypes.string,
      city: PropTypes.string,
      postcode: PropTypes.string,
      mobilePhone: PropTypes.string,
      phone: PropTypes.string,
    }),
    Package: PropTypes.shape({
      name: PropTypes.string,
    }),
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  firstCarComponents: PropTypes.array,
  secondCarComponents: PropTypes.array,
  breadcrumbs: PropTypes.array.isRequired,
};
const defaultProps = {
  loading: true,
  error: null,
  firstCarComponents: [],
  secondCarComponents: [],
};

const Quote = ({
  isFormVisible,
  setFormVisibility,
  loading,
  error,
  quote,
  firstCarComponents,
  secondCarComponents,
  car,
  car2,
  breadcrumbs,
  goBack,
}) => {
  if (loading) {
    return (
      <div className={s.root}>
        <span className="fa fa-spinner fa-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={s.root}>
        <Breadcrumbs paths={breadcrumbs} />
        <button type="button" className={s.back} onClick={goBack}>
          <div className="fa fa-arrow-left" />
          Back
        </button>
        <div className={s.error}>{error}</div>
      </div>
    );
  }

  return (
    <NavBar withPadding>
      <div className={s.root}>
        <Breadcrumbs paths={breadcrumbs} />
        <NoPrint>
          <button type="button" className={s.back} onClick={goBack}>
            <div className="fa fa-arrow-left" />
            Back
          </button>
        </NoPrint>
        <div className={s.title}>Service Plan Quote</div>
        <div className={s.subtitle}>
          Below is a tailored quote for your vehicle. To proceed and activate
          the plan just add customer details.
        </div>
        <PrintOnly>
          <h2>{quote.Provider.companyName}</h2>
          <div>
            {quote.Provider.addressLine1}
            {quote.Provider.addressLine2 &&
              `, ${quote.Provider.addressLine2}`}, {quote.Provider.city},{' '}
            {quote.Provider.postcode}
          </div>
          <div>{quote.Provider.mobilePhone || quote.Provider.phone}</div>
          <div>
            <a href={siteUrl}>{siteUrl}</a>
          </div>
        </PrintOnly>
        <div className={s.container}>
          {quote.VRN2 && <h2>First car</h2>}
          <QuoteCarInfo
            car={car}
            services={firstCarComponents}
            regNumber={quote.VRN}
            currentMileage={quote.currentMileage}
            lastServiceMileage={quote.lastServiceMileage}
            lastServiceDate={quote.lastServiceDate}
            withPlanDetails
          />
          {quote.VRN2 && (
            <div>
              <PageBreak />
              <h2>Second Car</h2>
              <QuoteCarInfo
                car={car2}
                services={secondCarComponents}
                regNumber={quote.VRN2}
                currentMileage={quote.VRN2CurrentMileage}
                lastServiceMileage={quote.VRN2LastServiceMileage}
                lastServiceDate={quote.VRN2LastServiceDate}
              />
            </div>
          )}
          <PrintElement avoidPageBreak>
            <PaymentDetails />
          </PrintElement>
          <NoPrint>
            <br />
            <br />
            {!isFormVisible && (
              <button
                type="button"
                className={s.accept_plan}
                onClick={() => setFormVisibility(true)}
              >
                Accept Plan and Proceed to Customer Details
              </button>
            )}
            {isFormVisible && <CustomerForm Package={quote.Package} />}
          </NoPrint>
        </div>
      </div>
    </NavBar>
  );
};

Quote.propTypes = propTypes;
Quote.defaultProps = defaultProps;

const mapStateToProps = ({
  quote: { quoteDetails, quoteLoading, firstCar, secondCar },
}) => {
  const { Components } = quoteDetails;

  return {
    quote: quoteDetails,
    loading: quoteLoading.loading,
    error: quoteLoading.error,
    car: firstCar,
    car2: secondCar,
    firstCarComponents: filter(Components, { carNumber: 1 }),
    secondCarComponents: filter(Components, { carNumber: 2 }),
  };
};

export default compose(
  withState('isFormVisible', 'setFormVisibility', false),
  withRouteParams('route'),
  connect(
    mapStateToProps,
    {
      goBack: actions.goBackToForm,
      getQuote: actions.getQuote,
    },
  ),
  lifecycle({
    componentDidMount() {
      const {
        getQuote,
        route: { id },
      } = this.props;

      getQuote(id);
    },
  }),
  withProps(() => ({
    breadcrumbs: [
      { name: 'Home', to: '/' },
      {
        name: 'Create A New Service Plan',
        to: '/provider/new-plan',
        react: true,
      },
      { name: 'Proceed to Customer Details' },
    ],
  })),
)(Quote);
